import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaGraduationCap, FaChartBar, FaMedal, FaPlusCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export default function Education() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchLeaderboard() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('https://dev.sliitleo.org/Backend/get_leaderboard.php');
        const data = await res.json();
        if (!data.success || !Array.isArray(data.leaderboard)) throw new Error('Invalid data');
        setLeaderboard(data.leaderboard);
      } catch (e) {
        setError('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    }
    fetchLeaderboard();
  }, []);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/default-user.png',
  };
  return (
    <div className="flex flex-col md:flex-row bg-[#111827] text-white min-h-screen">
      <Sidebar className="hidden md:block" />
      <Topbar user={user} />
      <main className="md:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-4 md:p-5 mb-6 md:mb-8 flex items-center gap-3 md:gap-4 shadow-lg">
          <span className="flex items-center gap-2 md:gap-3">
            <span className="bg-indigo-600 p-1.5 md:p-2 rounded-full shadow-lg flex items-center justify-center">
              <FaGraduationCap className="text-white text-xl md:text-2xl" />
            </span>
            <span className="text-base md:text-lg font-bold tracking-wide text-white drop-shadow">
              Education Admin<span className="text-gray-400 font-normal"> / Leaderboard</span>
            </span>
          </span>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Link to="/education-statistics" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-gray-700 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group">
              <span className="text-orange-400 text-lg sm:text-xl"><FaChartBar /></span>
              <span className="truncate">Statistics</span>
            </Link>
            <Link to="/education-leaderboard" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-indigo-600 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-indigo-500 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group">
              <span className="text-white text-lg sm:text-xl"><FaMedal /></span>
              <span className="truncate">Leaderboard</span>
            </Link>
            <Link to="/education-add-quiz-question" className="flex-1 sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold text-white border border-gray-700 shadow hover:scale-[1.02] sm:hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group">
              <span className="text-red-400 text-lg sm:text-xl"><FaPlusCircle /></span>
              <span className="truncate">Add Quiz</span>
            </Link>
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-[#23272f] rounded-xl p-4 sm:p-6 md:p-8 shadow-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-white">Leaderboard</h2>
          <div className="overflow-x-auto -mx-2 sm:-mx-4 md:mx-0">
            {loading ? (
              <div className="text-center text-gray-400 py-6 sm:py-8">Loading leaderboard...</div>
            ) : error ? (
              <div className="text-center text-red-400 py-6 sm:py-8">{error}</div>
            ) : (
              <div className="min-w-full inline-block align-middle">
                <table className="min-w-full divide-y divide-gray-800">
                  <thead className="hidden sm:table-header-group">
                    <tr className="bg-[#1a1d23]">
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Rank</th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {leaderboard.map((user, idx) => (
                      <tr 
                        key={user.id || user.name} 
                        className={`${idx === 0 ? 'bg-gradient-to-r from-indigo-900/60 to-purple-900/40' : ''} ${idx < 3 ? 'sm:bg-opacity-50' : ''}`}
                      >
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-base sm:text-lg font-bold">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap flex items-center gap-2 sm:gap-3">
                          <img 
                            src={user.avatar || '/avatars/default.png'} 
                            alt={user.name} 
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-indigo-400 shadow" 
                            onError={e => e.target.style.display='none'} 
                          />
                          <span className="text-sm sm:text-base font-medium sm:font-semibold text-white truncate max-w-[120px] sm:max-w-xs">
                            {user.name}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-green-400 font-bold text-base sm:text-lg">
                          {user.score}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
